import { ShowingRequestViewModel, ShowingSlot } from '@lumora/core';
import { useViewModel } from '@lumora/ui';
import { useEffect, useMemo } from 'react';
import { SITE, client } from '../../client';
import type Car from '../../models/Car';

interface AppointmentRequestModalProps {
    car: Pick<Car, 'id' | 'year' | 'make' | 'model'>;
    mode?: 'guest-request' | 'associate-schedule';
    onClose: () => void;
    onSubmitted?: () => void;
}

const allowMockAvailability = import.meta.env.VITE_SCHEDULING_MOCKS === 'true';

const formatDay = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
};

const formatTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const slotLabel = (slot: ShowingSlot) => slot.label ?? `${formatTime(slot.start)} - ${formatTime(slot.end)}`;

const mockSlots = (): ShowingSlot[] => {
    const today = new Date();
    const isoDay = (offset: number) => {
        const next = new Date(today);
        next.setDate(today.getDate() + offset);
        return next.toISOString().slice(0, 10);
    };

    return [
        new ShowingSlot('mock-full-day', isoDay(1), `${isoDay(1)}T09:00:00`, `${isoDay(1)}T17:00:00`, 'full-day', 'Open day, pick any time'),
        new ShowingSlot('mock-before-noon', isoDay(2), `${isoDay(2)}T09:00:00`, `${isoDay(2)}T12:00:00`, 'split', 'Morning availability'),
        new ShowingSlot('mock-after-noon', isoDay(2), `${isoDay(2)}T13:00:00`, `${isoDay(2)}T17:00:00`, 'split', 'Afternoon availability'),
    ];
};

export default function AppointmentRequestModal({
    car,
    mode = 'guest-request',
    onClose,
    onSubmitted,
}: AppointmentRequestModalProps) {
    const viewModel = useMemo(
        () => new ShowingRequestViewModel(client, SITE, car.id, {
            mode,
            fallbackSlots: allowMockAvailability ? mockSlots : undefined,
        }),
        [car.id, mode],
    );
    const state = useViewModel(viewModel);
    const { slots, selectedSlotId, phone, message, error, success } = state;
    const isLoading = state.loading;
    const isSubmitting = state.submitting;
    const isSubmitted = state.submitted;

    useEffect(() => {
        void viewModel.loadAvailability();
    }, [viewModel]);

    const title = mode === 'associate-schedule' ? 'Quick Schedule Showing' : 'Request A Showing';
    const submitLabel = mode === 'associate-schedule' ? 'Create Appointment' : 'Request Showing';

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (await viewModel.submit()) {
            window.dispatchEvent(new CustomEvent('appointments-updated'));
            onSubmitted?.();
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
            <section
                aria-modal="true"
                role="dialog"
                className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl"
                onClick={event => event.stopPropagation()}
            >
                <div className="flex items-start justify-between border-b border-gray-100 p-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-600">{car.year} {car.make} {car.model}</p>
                        <h2 className="mt-1 text-2xl font-black italic uppercase tracking-tight text-gray-950">{title}</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full bg-gray-100 px-3 py-2 text-sm font-black text-gray-600 hover:bg-gray-200" aria-label="Close appointment modal">
                        X
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col max-h-[80vh]">
                    <div className="overflow-y-auto p-6 space-y-6">
                        <div>
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Available Windows (Next 5 Days)</h3>
                                {isLoading && <span className="text-xs font-bold text-gray-400">Loading...</span>}
                            </div>

                            {!isLoading && slots.length === 0 && (
                                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5 text-sm font-bold text-gray-500">
                                    No showing slots are available right now.
                                </div>
                            )}

                            <div className="grid gap-3 sm:grid-cols-2">
                                {slots.map(slot => (
                                    <button
                                        type="button"
                                        key={slot.id}
                                        onClick={() => {
                                            viewModel.selectSlot(slot.id);
                                        }}
                                        className={`rounded-2xl border-2 p-4 text-left transition-all ${
                                            selectedSlotId === slot.id
                                                ? 'border-red-600 bg-red-50 shadow-lg scale-[1.02]'
                                                : 'border-gray-100 bg-white hover:border-gray-300'
                                        }`}
                                    >
                                        <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400">{formatDay(slot.date || slot.start)}</span>
                                        <span className="mt-1 block text-sm font-black text-gray-950">{slotLabel(slot)}</span>
                                        <span className="mt-2 inline-flex rounded-full bg-gray-100 px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500">
                                            {slot.kind === 'full-day' ? 'Full Day' : slot.kind === 'split' ? 'Split Window' : '60m Slot'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <label className="block">
                                <span className="text-xs font-black uppercase tracking-widest text-gray-500">Guest Phone *</span>
                                <input
                                    value={phone}
                                    onChange={event => {
                                        viewModel.setPhone(event.target.value);
                                    }}
                                    required
                                    inputMode="tel"
                                    pattern="(?:\+?1[\s.-]?)?(?:\([2-9][0-9]{2}\)|[2-9][0-9]{2})[\s.-]?[2-9][0-9]{2}[\s.-]?[0-9]{4}"
                                    className="mt-2 w-full rounded-xl border-2 border-gray-100 p-4 font-bold outline-none focus:border-red-600 transition"
                                    placeholder="(507) 555-1234"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-black uppercase tracking-widest text-gray-500">Notes Optional</span>
                                <textarea
                                    value={message}
                                    onChange={event => viewModel.setMessage(event.target.value)}
                                    className="mt-2 h-20 w-full resize-none rounded-xl border-2 border-gray-100 p-4 text-sm font-bold outline-none focus:border-red-600 transition"
                                    placeholder="e.g. Bringing my trade-in"
                                />
                            </label>
                        </div>

                        {error && <p className="rounded-xl bg-amber-50 p-4 text-xs font-bold text-amber-800 border border-amber-100">{error}</p>}
                        {success && <p className="rounded-xl bg-green-50 p-4 text-xs font-bold text-green-800 border border-green-100">{success}</p>}
                    </div>

                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end border-t border-gray-100 p-6 bg-gray-50/50">
                        <button type="button" onClick={onClose} className="rounded-xl bg-white border-2 border-gray-100 px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-50 transition">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitted || isSubmitting || isLoading || slots.length === 0}
                            className="rounded-xl bg-red-600 px-8 py-4 text-[10px] font-black uppercase tracking-widest text-white hover:bg-red-700 shadow-xl shadow-red-100 disabled:cursor-not-allowed disabled:opacity-50 transition transform active:scale-95"
                        >
                            {isSubmitted ? 'Submitted' : isSubmitting ? 'Processing...' : submitLabel}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}
